import { useEffect, useState } from "react";

import { Trans } from "@lingui/macro";
import { useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";

import Collapse from "@mui/material/Collapse";

import Setting from "@app/analytics/account/components/Setting";
import SettingAlert from "@app/analytics/account/components/SettingAlert";
import SettingContent from "@app/analytics/account/components/SettingContent";
import SettingsModule from "@app/analytics/account/components/SettingsModule";
import {
  Button,
  Divider,
  SettingSubtext,
  SettingSubtextHeader,
} from "@app/analytics/account/components/styles";
import type { SettingModule } from "@app/analytics/account/types";
import SSOItem from "@app/analytics/account/UserPreferences/AccountModule/SSOItem";
import FirmographicsForm from "@app/analytics/components/FirmographicsForm";
import useAccountSettings from "@app/analytics/hooks/settings/useAccountSettings";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import { useAuthenticated } from "@app/common/auth/useAuth";
import { DEPARTMENTS, ROLE_LEVELS } from "@app/common/constants/firmographics";
import { SSO_PROVIDERS } from "@app/common/constants/providers";
import { showToast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import { isResponseErrorWithStatus } from "@app/common/utils";
import { yupResolver } from "@app/utils/forms/yupResolver";

import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import Link from "@rui/Link";
import Stack, { Row } from "@rui/Stack";
import { Italic } from "@rui/styles";

import EmailForm from "./EmailForm";
import ProfileForm from "./ProfileForm";

const emailValidationSchema = yup.object().shape({
  email: yup.string().email("Please enter a valid email"),
});

const nameValidationSchema = yup.object().shape({
  firstName: yup.string().required("Cannot be empty"),
  lastName: yup.string().required("Cannot be empty"),
});

interface AccountModuleProps extends SettingModule {
  onEmailUpdate?: (email: string) => void;
}

const getEmailErrorMessage = (
  errorMessage?: string | { message?: string } | null
) => {
  if (!errorMessage) return null;
  if (typeof errorMessage === "string") return errorMessage;
  return errorMessage.message ?? null;
};

const AccountModule = ({ onEmailUpdate, ...props }: AccountModuleProps) => {
  const [showNameInput, setShowNameInput] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [showFirmographicsInput, setShowFirmographicsInput] = useState(false);

  const { useUpdateProfileInfo } = useAccountSettings();
  const auth = useAuthenticated();
  const [queryParams] = useSearchParams();
  const ssoShowAlert =
    queryParams.get("action") === "add_sso" && queryParams.get("success");
  const ssoAlertSuccess = queryParams.get("success") === "true";
  const ssoAlertReason = queryParams.get("reason");

  useEffect(() => {
    if (!ssoShowAlert) return;
    if (ssoAlertSuccess) {
      showToast({
        message: "Your account was successfully connected!",
        severity: "success",
      });
    } else {
      showToast({
        message:
          ssoAlertReason === "duplicate"
            ? "Sorry, that account is already associated with an existing Read account."
            : "Sorry, we couldn't connect your account. Please try again or contact support.",
        severity: "error",
      });
    }
  }, [ssoShowAlert, ssoAlertSuccess, ssoAlertReason]);

  const { useRequestPasswordReset, useUpdateEmail } = useAccountSettings();
  const {
    connectedAccountProviders,
    deleteConnectedProvider,
    addAccountProvider,
    isLoading: isInitialLoadingAccountProviders,
  } = useConnectedProviders();

  const form = useForm({ resolver: yupResolver(nameValidationSchema) });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(emailValidationSchema),
  });

  const newEmail = useWatch({ control, name: "email" });

  const toggleEmailInput = () => {
    setShowEmailInput(!showEmailInput);
  };

  const toggleFirmographicsInput = () => {
    setShowFirmographicsInput(!showFirmographicsInput);
  };

  const toggleNameInput = () => {
    setShowNameInput(!showNameInput);
  };

  const handleSubmitCleanup = (setting: "email" | "name") => {
    auth.fetchAndSetUser(); // check for updates so we can display to user

    if (setting === "email") {
      toggleEmailInput(); // hide input
      useUpdateEmail.reset();
    } else {
      toggleNameInput();
      useUpdateProfileInfo.reset();
    }
  };

  const onNameSubmit = (data: any) => {
    useUpdateProfileInfo.mutate(
      {
        firstName: data.firstName,
        lastName: data.lastName,
      },
      {
        onSuccess: () => {
          showToast({
            message: "Successfully changed your info!",
            severity: "success",
          });
        },
        onError: () => {
          showToast({
            message:
              "Something went wrong changing your info. Please try again later",
            severity: "error",
          });
        },
        onSettled: () => {
          handleSubmitCleanup("name");
        },
      }
    );
    form.unregister(["firstName", "lastName"]);
  };

  const onSubmit = (data: any) => {
    useUpdateEmail.mutate(data.email, {
      onSuccess: () => {
        onEmailUpdate?.(data.email);
        showToast({
          message: `A verification email has been sent to ${newEmail}. Click the magic link in the confirmation email to verify your email address`,
          severity: "success",
        });
        setTimeout(() => handleSubmitCleanup("email"), 3000);
      },
      onError: (error) => {
        if (isResponseErrorWithStatus(error, 409)) {
          form.setValue("email", "");
          showToast({
            message:
              "Sorry, an account with that email address already exists.",
            severity: "warning",
          });
          return;
        }

        showToast({
          message:
            "Something went wrong updating your email. Please check that you've entered a valid email.",
          severity: "error",
        });
      },
    });
  };

  const handleRequestPasswordReset = async () => {
    useRequestPasswordReset.mutate(
      { email: auth.user.email, recaptchaValue: null },
      {
        onSuccess: () => {
          showToast({
            message:
              "A password reset link has been sent to your email. Click the magic link in email to update password.",
            severity: "success",
          });
        },
        onError: () => {
          showToast({
            message:
              "Something went wrong sending a password reset email. Please try again later.",
            severity: "error",
          });
        },
      }
    );
  };

  const emailError =
    !!errors.email || Boolean((auth.errors.signUp || {}).email);

  const emailFieldError = errors.email?.message
    ? errors.email.message
    : emailError
      ? auth.errors.signUp?.email
      : null;

  const hasConnectedSSOProviders =
    isInitialLoadingAccountProviders || connectedAccountProviders.length;
  const canAddAdditionalSSOProvider =
    !isInitialLoadingAccountProviders &&
    connectedAccountProviders.length < SSO_PROVIDERS.length;

  return (
    <SettingsModule {...props}>
      <Setting title="Your Account">
        <SettingContent sx={{ pt: 2 }}>
          <Collapse in={!showNameInput} sx={{ width: "100%" }}>
            <Row variant="apart" spacing={2}>
              <Stack spacing={1}>
                <SettingSubtextHeader>
                  <Trans>Name</Trans>
                </SettingSubtextHeader>
                <SettingSubtext
                  variant="bodySecondaryReg"
                  sx={{ color: "shades.neutral.500" }}
                >
                  {auth.user.fullName}
                </SettingSubtext>
              </Stack>
              <Button onClick={toggleNameInput}>
                <Trans>Update</Trans>
              </Button>
            </Row>
          </Collapse>
          <Collapse in={showNameInput} sx={{ width: "100%" }}>
            <Row variant="apart" spacing={2}>
              <ProfileForm
                onSubmit={form.handleSubmit(onNameSubmit)}
                form={form}
                onCancel={toggleNameInput}
                status={useUpdateProfileInfo}
              />
            </Row>
          </Collapse>
          <Divider sx={{ my: 3 }} />
          <Collapse in={!showFirmographicsInput} sx={{ width: "100%" }}>
            <Row variant="apart" spacing={2}>
              <Stack spacing={2}>
                <Stack spacing={1}>
                  <SettingSubtextHeader>
                    <Trans>Job title</Trans>
                  </SettingSubtextHeader>
                  <SettingSubtext
                    variant="bodySecondaryReg"
                    sx={{ color: "shades.neutral.500" }}
                  >
                    {auth.user.jobTitle ?? (
                      <Italic>
                        <Trans>Not provided</Trans>
                      </Italic>
                    )}
                  </SettingSubtext>
                </Stack>
                <Stack spacing={1}>
                  <SettingSubtextHeader>
                    <Trans>Role level</Trans>
                  </SettingSubtextHeader>
                  <SettingSubtext
                    variant="bodySecondaryReg"
                    sx={{ color: "shades.neutral.500" }}
                  >
                    {auth.user.jobRoleLevel ? (
                      getSelectedFromOptions(
                        ROLE_LEVELS,
                        auth.user.jobRoleLevel
                      )!.label
                    ) : (
                      <Italic>
                        <Trans>Not provided</Trans>
                      </Italic>
                    )}
                  </SettingSubtext>
                </Stack>
                <Stack spacing={1}>
                  <SettingSubtextHeader>
                    <Trans>Department</Trans>
                  </SettingSubtextHeader>
                  <SettingSubtext
                    variant="bodySecondaryReg"
                    sx={{ color: "shades.neutral.500" }}
                  >
                    {auth.user.jobDepartment ? (
                      getSelectedFromOptions(
                        DEPARTMENTS,
                        auth.user.jobDepartment
                      )!.label
                    ) : (
                      <Italic>
                        <Trans>Not provided</Trans>
                      </Italic>
                    )}
                  </SettingSubtext>
                </Stack>
              </Stack>
              <Button onClick={toggleFirmographicsInput}>
                <Trans>Update</Trans>
              </Button>
            </Row>
          </Collapse>
          <Collapse in={showFirmographicsInput} sx={{ width: "100%" }}>
            <Stack variant="left" spacing={2}>
              <FirmographicsForm
                onCloseForm={toggleFirmographicsInput}
                onSuccess={() => auth.fetchAndSetUser()}
                disclaimer="This information is kept private and is only used for account customization"
                defaultValues={
                  // if user has a job title, we can safely assume they'll have info for all other job fields
                  auth.user.jobTitle
                    ? {
                        jobRoleLevel: auth.user.jobRoleLevel,
                        jobDepartment: auth.user.jobDepartment,
                        jobTitle: auth.user.jobTitle,
                      }
                    : undefined
                }
              />
            </Stack>
          </Collapse>
          <Divider sx={{ my: 3 }} />
          <Collapse in={!showEmailInput} sx={{ width: "100%" }}>
            <Stack spacing={2}>
              <SettingAlert
                severity="warning"
                persist
                show={!auth.user.hasEmailAccount}
              >
                Your primary email cannot be changed because it is connected to
                an SSO account. To update it, you must first add a password
                below.
              </SettingAlert>
              <Row variant="apart" spacing={2}>
                <Stack spacing={1}>
                  <SettingSubtextHeader>
                    <Trans>Primary Email</Trans>
                  </SettingSubtextHeader>
                  <SettingSubtext
                    variant="bodySecondaryReg"
                    sx={{ color: "shades.neutral.500" }}
                  >
                    {auth.user.email}
                  </SettingSubtext>
                </Stack>
                <Button
                  onClick={toggleEmailInput}
                  disabled={!auth.user.hasEmailAccount}
                >
                  <Trans>Update</Trans>
                </Button>
              </Row>
            </Stack>
          </Collapse>
          <Collapse in={showEmailInput} sx={{ width: "100%" }}>
            <Row sx={{ alignSelf: "flex-start" }}>
              <EmailForm
                error={emailError}
                helperText={getEmailErrorMessage(emailFieldError)}
                inputProps={{ ...register("email") }}
                disabled={useUpdateEmail.isSuccess}
                onCancel={toggleEmailInput}
                onSubmit={handleSubmit(onSubmit)}
              />
            </Row>
          </Collapse>
          <Divider sx={{ my: 3 }} />
          <Row variant="apart" sx={{ width: "100%" }} spacing={2}>
            <SettingSubtextHeader>Password</SettingSubtextHeader>
            <Button
              onClick={handleRequestPasswordReset}
              disabled={useRequestPasswordReset.isSuccess}
              sx={{
                minWidth: "auto",
                borderColor: "shades.neutral.100",
                whiteSpace: "nowrap",
              }}
            >
              {auth.user.hasEmailAccount ? "Reset" : "Add"} Password
            </Button>
          </Row>
        </SettingContent>
      </Setting>
      <Divider />
      <Setting
        title="Single Sign-On (SSO)"
        subtitle="Authorizing single sign-on allows you to sign in to Read using the credentials associated with the linked account."
      >
        <SettingContent spacing={2}>
          {hasConnectedSSOProviders ? (
            <Stack sx={{ width: "100%" }} spacing={1}>
              {connectedAccountProviders.map((provider) => (
                <SSOItem
                  key={provider.providerId}
                  providerId={provider.providerId}
                  onRemove={
                    !auth.user.hasEmailAccount &&
                    connectedAccountProviders.length < 2
                      ? // don't let user remove last sso account if also has no email account
                        undefined
                      : deleteConnectedProvider.mutate
                  }
                  reauthRequired={provider.reauthenticationRequired}
                  onReauthClick={addAccountProvider}
                />
              ))}
              {canAddAdditionalSSOProvider && (
                <Link to={ROUTES.SSO_ADD} sx={{ width: "100%", mt: 1 }}>
                  <Button sx={{ width: "100%" }}>Add SSO Account</Button>
                </Link>
              )}
            </Stack>
          ) : (
            <Row variant="apart" sx={{ width: "100%" }}>
              <SettingSubtextHeader>
                No SSO account enabled
              </SettingSubtextHeader>
              <Link to={ROUTES.SSO_ADD}>
                <Button
                  sx={{
                    minWidth: "auto",
                    bgcolor: "shades.ghost.500",
                    borderColor: "shades.ghost.500",
                  }}
                >
                  Add SSO
                </Button>
              </Link>
            </Row>
          )}
        </SettingContent>
      </Setting>
    </SettingsModule>
  );
};

export default AccountModule;
